import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import { Box, Chip, Typography } from "@mui/material"

import { useDropzone } from "react-dropzone"

type PDFDropzoneProps = {
  onFileSelect: (files: File[], base64Strings: string[]) => void
  selectedFiles: File[]
  label?: string
  dragActiveLabel?: string
  onRemoveFile?: (index: number) => void
  filesOnTop?: boolean
}

export const PDFDropzone = ({
  onFileSelect,
  selectedFiles,
  label = "PDF-Dateien hierher ziehen oder klicken zum Auswählen",
  dragActiveLabel = "PDF-Dateien hier ablegen...",
  onRemoveFile,
  filesOnTop = false,
}: PDFDropzoneProps) => {
  const onDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      try {
        const base64Promises = acceptedFiles.map(convertToBase64)
        const base64Strings = await Promise.all(base64Promises)
        onFileSelect(acceptedFiles, base64Strings)
      } catch (error) {
        console.error("Fehler beim Konvertieren der Dateien:", error)
      }
    }
  }

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        if (typeof reader.result === "string") {
          // Entferne den Data URL Prefix (z.B. "data:application/pdf;base64,")
          const base64String = reader.result.split(",")[1]
          resolve(base64String)
        } else {
          reject(new Error("Fehler beim Lesen der Datei"))
        }
      }
      reader.onerror = (error) => reject(error)
    })
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
    },
    multiple: true,
  })

  const handleRemoveFile = (index: number) => {
    if (onRemoveFile) {
      onRemoveFile(index)
      return
    }
    const newFiles = [...selectedFiles]
    newFiles.splice(index, 1)
    onFileSelect(newFiles, []) // Leere base64Strings, da wir sie nicht mehr benötigen
  }

  return (
    <Box>
      {selectedFiles.length > 0 && filesOnTop && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {selectedFiles.map((file, index) => (
            <Chip
              key={`${file.name}-${index}`}
              icon={<PictureAsPdfIcon />}
              label={file.name}
              onDelete={() => handleRemoveFile(index)}
            />
          ))}
        </Box>
      )}
      <Box
        {...getRootProps()}
        sx={{
          border: "2px dashed",
          borderColor: isDragActive ? "primary.main" : "grey.300",
          borderRadius: 1,
          p: 3,
          textAlign: "center",
          cursor: "pointer",
          "&:hover": {
            borderColor: "primary.main",
          },
        }}
      >
        <input {...getInputProps()} />
        <Typography variant="body2" color="text.secondary">
          {isDragActive ? dragActiveLabel : label}
        </Typography>
      </Box>
      {selectedFiles.length > 0 && !filesOnTop && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2, mt: 2 }}>
          {selectedFiles.map((file, index) => (
            <Chip key={`${file.name}-${index}`} icon={<PictureAsPdfIcon />} label={file.name} />
          ))}
        </Box>
      )}
    </Box>
  )
}
